// Ativar uma marca: as plataformas da ativação, o que o dono da marca precisa liberar em cada
// uma e o pedido pronto para mandar a ele. Regras puras (ativacao.test.ts).

export type PlataformaAtivacao = 'meta_ads' | 'instagram' | 'facebook' | 'google_ads' | 'ga4' | 'search_console'

/** Na ordem da tela. TikTok entra quando houver coletor. */
export const PLATAFORMAS_ATIVACAO: Array<{ id: PlataformaAtivacao; nome: string; grupo: 'Meta' | 'Google' }> = [
  { id: 'meta_ads', nome: 'Meta Ads', grupo: 'Meta' },
  { id: 'instagram', nome: 'Instagram', grupo: 'Meta' },
  { id: 'facebook', nome: 'Página do Facebook', grupo: 'Meta' },
  { id: 'google_ads', nome: 'Google Ads', grupo: 'Google' },
  { id: 'ga4', nome: 'Google Analytics 4', grupo: 'Google' },
  { id: 'search_console', nome: 'Search Console', grupo: 'Google' },
]

/** O que o sistema mostra ao dono da marca (nada disso é segredo). Null = ainda não se sabe. */
export type InfoAtivacao = {
  emailContaServico: string | null
  mcc: string | null
  portfolioMeta: { id: string; nome: string } | null
}

const PEDIR = '(peça ao administrador do MarketingHUB)'

/** "1234567890" → "123-456-7890", como o Google Ads mostra. */
export const formatarMcc = (id: string) => (/^\d{10}$/.test(id) ? `${id.slice(0, 3)}-${id.slice(3, 6)}-${id.slice(6)}` : id)

/** O passo do dono da marca para liberar o acesso, em uma plataforma. */
export function instrucaoDoDono(p: PlataformaAtivacao, info: InfoAtivacao): string {
  const portfolio = info.portfolioMeta ? `${info.portfolioMeta.id} (${info.portfolioMeta.nome})` : `do MarketingHUB ${PEDIR}`
  const email = info.emailContaServico ?? `do MarketingHUB ${PEDIR}`
  switch (p) {
    case 'meta_ads':
      return `No Gerenciador de Negócios da marca: Configurações do negócio → Contas → Contas de anúncios → a conta da marca → Atribuir parceiros → informe o ID do portfólio ${portfolio} e libere "Ver desempenho" (ou "Gerenciar campanhas", para usar o Aplicar).`
    case 'instagram':
      return `O Instagram precisa ser conta profissional e estar vinculado à página do Facebook da marca. No Gerenciador de Negócios: Contas → Contas do Instagram → a conta → Atribuir parceiros → ID do portfólio ${portfolio}. Compartilhe também a página do Facebook ligada a ele (Contas → Páginas → Atribuir parceiros → o mesmo ID): é por ela que o Instagram aparece para o sistema.`
    case 'facebook':
      return `No Gerenciador de Negócios: Contas → Páginas → a página da marca → Atribuir parceiros → ID do portfólio ${portfolio}, liberando ver o desempenho (insights).`
    case 'google_ads':
      return `Aceitar o pedido de vínculo da conta de administrador (MCC) ${info.mcc ? formatarMcc(info.mcc) : PEDIR}: na conta do Google Ads da marca, Adm. → Acesso e segurança → Administradores → Aceitar. O pedido é enviado pela equipe do MarketingHUB a partir da MCC e só aparece para aceitar depois de aprovado por um segundo administrador da MCC.`
    case 'ga4':
      return `No Google Analytics: Administrador → Gerenciamento de acesso da conta → + → Adicionar usuários → e-mail ${email} → função Leitor (desmarque "notificar por e-mail").`
    case 'search_console':
      return `No Search Console, na propriedade do site: Configurações → Usuários e permissões → Adicionar usuário → e-mail ${email} → permissão Restrita.`
  }
}

/** Depois que o dono libera no Meta, ainda falta este passo do lado do MarketingHUB. */
export const PASSO_ADMIN_META =
  'Depois que o dono compartilhar, um administrador do portfólio do MarketingHUB atribui a conta ao usuário do sistema (Usuários do sistema → Atribuir ativos). Aí ela aparece no "Buscar no Meta".'

/** O que o acesso permite, sem prometer "só leitura" onde não é (MCC e "Gerenciar campanhas" alteram campanhas). */
function avisoDeAcesso(plataformas: PlataformaAtivacao[]): string {
  const google = plataformas.includes('google_ads')
  const meta = plataformas.includes('meta_ads')
  if (!google && !meta) return 'Nenhuma senha é compartilhada: o acesso é só de leitura dos resultados e pode ser retirado a qualquer momento. Obrigado!'
  const onde = google && meta ? 'No Google Ads (e no Meta Ads, se você liberar "Gerenciar campanhas")' : google ? 'No Google Ads' : 'No Meta Ads, se você liberar "Gerenciar campanhas"'
  return `Nenhuma senha é compartilhada. ${onde}, o MarketingHUB também poderá pausar campanhas e ajustar orçamentos, sempre com confirmação de uma pessoa; nas demais plataformas o acesso é só de leitura. Tudo pode ser retirado a qualquer momento. Obrigado!`
}

/** O pedido completo ao dono da marca, para copiar e mandar por e-mail ou WhatsApp. */
export function pedidoAoDono(marca: string, plataformas: PlataformaAtivacao[], info: InfoAtivacao): string {
  if (!plataformas.length) return ''
  const nomes = new Map(PLATAFORMAS_ATIVACAO.map((p) => [p.id, p.nome]))
  const passos = plataformas.map((p, i) => `${i + 1}. ${nomes.get(p)}: ${instrucaoDoDono(p, info)}`)
  return [
    `Olá! Para conectar a ${marca} ao MarketingHUB (o painel de marketing do grupo), precisamos que você libere o acesso às contas da marca:`,
    '',
    ...passos,
    '',
    avisoDeAcesso(plataformas),
  ].join('\n')
}
